import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { namespace } from '@common-config/constants';
import { ChooseStep, LocalStep, RemoteStep } from './connect-steps';
import { FirstMessageStep } from './first-message-step';
import { C, PreviewIllustration, Progress } from './onboarding-ui';
import { ConnectFlowModel, useWizardEffects } from './use-connect-flow';

/**
 * The connect wizard — the same on /onboarding (full screen) and /connections (in the card):
 * progress on top, then the screen for the first condition the user hasn't met yet.
 * `wide` = a roomy host: the live preview illustration sits beside the local steps.
 */
export const ConnectFlow: React.FC<{ model: ConnectFlowModel; wide?: boolean }> = ({ model: m, wide }) => {
  const { t } = useTranslation(namespace);
  const o = (key: string, opts?: Record<string, unknown>) => t(`onboarding.${key}`, opts);
  const g = (key: string) => t(`enginesManagement.guide.${key}`);
  const { justConnected } = useWizardEffects(m);

  if (!m.ready) {
    return (
      <div aria-busy style={{ display: 'flex', justifyContent: 'center', padding: '64px 0', color: C.muted }}>
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  const { screen, progress } = m.flow;
  let body: React.ReactNode;
  if (screen === 'choose') {
    body = <ChooseStep device={m.device} proPrice={m.proPrice} onChoose={m.actions.choose} onShareLink={m.actions.shareLink} onVideo={m.actions.video} />;
  } else if (screen === 'local') {
    const steps = (
      <LocalStep
        substate={m.substate}
        canTellAccount={m.canTellAccount}
        email={m.email}
        link={m.actions.link}
        onLinkAccount={m.actions.linkAccount}
        onGoToWhatsApp={m.actions.goToWhatsApp}
        onBack={m.actions.back}
        onTrack={m.track}
      />
    );
    body = wide ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 48 }}>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>{steps}</div>
        <div className="onb-illo-slot" style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', padding: '16px 24px' }}>
          <PreviewIllustration message={g('illoMessage')} scheduled={g('illoScheduled')} delivered={g('illoDelivered')} scheduledChip={g('scheduledChip')} />
        </div>
      </div>
    ) : steps;
  } else if (screen === 'remote') {
    body = (
      <RemoteStep
        loading={m.remote.loading}
        isPro={m.isPro}
        proPrice={m.proPrice}
        supportsLocal={m.device === 'chrome'}
        engine={m.remote.engine}
        expired={m.remote.expired}
        isCreating={m.remote.isCreating}
        atLimit={m.remote.atLimit}
        trial={m.remote.trial}
        onStartTrial={m.actions.startTrial}
        onManage={m.actions.manage}
        onCreate={m.actions.createRemote}
        onUpgrade={m.actions.upgrade}
        onUseLocal={m.actions.useLocal}
        onBack={m.actions.remoteBack}
      />
    );
  } else {
    body = (
      <FirstMessageStep
        ownWid={m.message.ownWid}
        findingNumber={m.message.findingNumber}
        kind={m.kind === 'remote' ? 'remote' : 'local'}
        justConnected={justConnected}
        alreadyDone={m.message.alreadyDone}
        isPro={m.isPro}
        proPrice={m.proPrice}
        startedAt={m.startedAt}
        onScheduleReal={m.actions.scheduleReal}
        onAlwaysOn={m.actions.alwaysOnFromDone}
        onTrack={m.track}
        onDelivered={m.actions.delivered}
      />
    );
  }

  return (
    // One readable column wherever it is hosted — wider only where the illustration sits beside the steps.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, width: '100%', maxWidth: wide && screen === 'local' ? 1040 : 680, marginInline: 'auto' }}>
      <Progress
        pct={progress.pct}
        step={progress.step}
        done={progress.done}
        ariaLabel={o('progress.label')}
        labels={[o('progress.connect'), o('progress.message'), o('progress.done')]}
        meterLabel={progress.done ? o('progress.meterDone') : o('progress.meter', { current: progress.step })}
      />
      <div key={screen === 'done' ? 'message' : screen}>{body}</div>
    </div>
  );
};
